import { createSlice } from "@reduxjs/toolkit";
import isEqual from 'lodash/isEqual';
import { RootState } from "../store";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AuthState } from "./types";
import {validateLogon} from '../../common/utils/validationRules';
import { trackEvent } from "../../utils/analytics";

type initialStateType = {
  authState: AuthState;
};
const authState: AuthState = {
  user: null,
  tokens: {
    accessToken: null,
    refreshToken: null,
  },
  loggedIn: false,
  errorDialog: {
    title: null,
    message: null,
    show: false,
  },
  logonParams: {
    phoneNumber: null,
    password: null,
  },
  loading: false,
  refreshing: false,
  inputErrors: {
    phoneNumber: null,
    password: null,
  },
  canLogin: false,
  feedbackMessage: {
    open: false,
    message: null,
    isError: false,
    shouldClose: false,
  },
  checkSubmit: false,
};

const initialState: initialStateType = {
  authState,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.authState.loading = action.payload;
    },
    showAlert: (state, action) => {
      state.authState.errorDialog = action.payload;
    },
    setLogonParams: (state, action) => {
      if (action.payload.values && !isEqual(state.authState.logonParams, action.payload.values)) {
        state.authState.logonParams = {
          ...state.authState.logonParams,
          ...action.payload.values
        };
        state.authState.canLogin = true;
      }
    },
    
    setInputErrors: (state, action) => {
      let errors = action.payload.errors;
      if (!errors || Object.keys(errors).length === 0) {
        errors = initialState.authState.inputErrors;
      }
      if (!isEqual(state.authState.inputErrors, errors)) {
        state.authState.inputErrors = { ...state.authState.inputErrors, ...errors };
        const { inputErrors, logonParams } = state.authState;
        state.authState.canLogin = Object.values(inputErrors).every(error => !error) &&
                          Object.values(logonParams).every(param => param !== null && param !== '');
      }
    },
    checkCanLogin: (state) => {
     
    },
    login: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.authState.loading = true;
      } else if (action.payload.methodRequestState === 'finished') {
        if (action.payload.result) {
          const {user, tokens} = action.payload.result;
          trackEvent("login-success", {
            state: 'from redux',
            user: user?.cnt_id || 'unknown',
          });
          state.authState.feedbackMessage = {
            message: 'Successfully logged in, Welcome!',
            isError: false,
            open: true,
            shouldClose: true,
          };
          state.authState.user = user;
          state.authState.tokens = {
            accessToken: tokens.access.token,
            refreshToken: tokens.refresh.token,
          };
          state.authState.logonParams = initialState.authState.logonParams;
          state.authState.loggedIn = true;
          sessionStorage.setItem('token', tokens.refresh.token);
        } else {
          trackEvent("login-failed", {
            user: state.authState.logonParams?.phoneNumber || 'unknown',
            state: 'from redux',
            error: `Error: ${action.payload.error?.message || 'Something went wrong'}`,
          });
          state.authState.feedbackMessage = {
            message: action.payload.error?.message || 'Something went wrong',
            isError: true,
            open: true,
            shouldClose: false,
          };
        }
        state.authState.loading = false;
      }
      // set user params & tokens
    },
    refreshApp: (state, action) => {
      // use refresh token to re-auth user on reload
     
      if (action.payload.methodRequestState === 'requested') {
        state.authState.loading = true;
        state.authState.refreshing = true;
      } else if (action.payload.methodRequestState === 'finished' && action.payload.result) {
        const {user, tokens} = action.payload.result;
        state.authState.user = user;
        state.authState.tokens = {
          accessToken: tokens.access.token,
          refreshToken: tokens.refresh.token,
        };
        state.authState.loggedIn = true;
        state.authState.refreshing = false;
        state.authState.loading = false;
        sessionStorage.setItem('token', tokens.refresh.token);
      } else {
        state.authState.feedbackMessage = {
          message: action.payload.error?.message || 'Something went wrong',
          isError: true,
          open: true,
          shouldClose: false,
        };
        state.authState.loading = false;
        state.authState.refreshing = false;
      }
    },
    logout: (state) => {
      trackEvent("logout", {
        user: state.authState.user?.cnt_id || 'unknown',
        phoneNumber: state.authState.logonParams?.phoneNumber || 'unknown',
      });
      // logout user
      state.authState.loggedIn = false;
      state.authState.user = null;
      state.authState.tokens = {
        accessToken: null,
        refreshToken: null,
      };
      sessionStorage.removeItem('token');
      state.authState.feedbackMessage = {
        message: null,
        isError: false,
        open: false,
        shouldClose: false,
      };
      state.authState.loading = false;
    },
    resetLoginFeedbackMessage: (state, action) => {
      state.authState.feedbackMessage = {
        message: null,
        isError: false,
        open: false,
        shouldClose: false,
      };
    },
  },
});

export const { setLoading, showAlert, login, refreshApp, logout, setLogonParams, setInputErrors, checkCanLogin, resetLoginFeedbackMessage } = authSlice.actions;


export const loginAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: login.type,
  payload: {
    params,
    method,
  }
});

export const refreshAppAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: refreshApp.type,
  payload: {
    params,
    method,
  }
});



export const selectAuthState = (state: RootState) => state.auth.authState;
